import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Card, Empty, PageHeader, Popconfirm, Space, Spin, Tag, Typography,
} from 'antd';
import { ApiOutlined, CopyOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';
import 'antd/dist/antd.css';
import '../../../App.scss';

import { useClient } from '../../../models/useClient';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useMyOAuthGrants } from '../../../models/useMyOAuthGrants';
import { useAccessibleClients } from '../../../models/useAccessibleClients';
import { payerApiOrigin, PAYER_ORIGIN_MIXED_CONTENT_MESSAGE } from '../../../utils/payerApiOrigin';
import { MCP_SERVER_URL, CONNECTOR_NAME, useMcpLive } from '../../../utils/aiConnections';
import { SCOPE_DESCRIPTIONS } from '../auth/ClientOAuthAuthorizeScreen';

const { Text, Paragraph, Title } = Typography;

export { MCP_SERVER_URL, CONNECTOR_NAME };

const STATUS_TAG = {
  active: { color: 'green', label: 'Active' },
  paused: { color: 'orange', label: 'Paused' },
  revoked: { color: 'default', label: 'Disconnected' },
};

export function formatWhen(ts) {
  const ms = ts?.toMillis?.();
  if (!ms) {
    return 'never';
  }
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** POST strike-payer `/oauth/grants/:id/:action` as the signed-in person (or an admin). */
export async function sendGrantAction(grantId, action) {
  const base = payerApiOrigin();
  if (!base) {
    throw new Error(PAYER_ORIGIN_MIXED_CONTENT_MESSAGE);
  }
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error('Not signed in');
  }
  const idToken = await user.getIdToken();
  const res = await fetch(`${base}/v2/api/oauth/grants/${encodeURIComponent(grantId)}/${action}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error_description || body.error || res.statusText || 'Request failed');
  }
  return body;
}

function SetupCard({ live }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard refused: the URL is selectable text below */
    }
  };
  return (
    <Card title="Connect Claude" style={{ marginBottom: 16 }} data-testid="ai-setup-card">
      {live !== true ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Not live yet"
          description="The Strikepay connector is being set up. Your connections will appear here as soon as it opens."
        />
      ) : null}
      <Paragraph style={{ marginBottom: 8 }}>
        Add Strikepay as a custom connector in Claude, then sign in with this account and choose what
        it can see. Nothing is shared until you approve.
      </Paragraph>
      <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 12 }}>
        <Text type="secondary">Connector name</Text>
        <Text strong>{CONNECTOR_NAME}</Text>
        <Text type="secondary">MCP server URL</Text>
        <Space wrap>
          <Text code copyable={false} data-testid="mcp-url">{MCP_SERVER_URL}</Text>
          <Button size="small" icon={<CopyOutlined />} onClick={copy}>{copied ? 'Copied' : 'Copy URL'}</Button>
        </Space>
      </Space>
      <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
        <li>In Claude, open Customize, then Connectors, then Add custom connector.</li>
        <li>Paste the name and URL above, then sign in to Strikepay with this account.</li>
        <li>Review what it can access, pick the teams to include, and approve.</li>
      </ol>
    </Card>
  );
}

function GrantCard({
  grant, clientNames, isAdmin, onAction, busy,
}) {
  const status = STATUS_TAG[grant.status] || { color: 'default', label: grant.status };
  const scopes = Array.isArray(grant.scopes) ? grant.scopes : [];
  const teams = Array.isArray(grant.strikeClientIds) ? grant.strikeClientIds : [];
  const revoked = grant.status === 'revoked';
  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      data-testid={`grant-${grant.id}`}
      title={(
        <Space>
          <ApiOutlined />
          <span>{grant.clientName || 'AI client'}</span>
          <Tag color={status.color}>{status.label}</Tag>
        </Space>
      )}
      extra={revoked ? null : (
        <Space>
          {!isAdmin && grant.status === 'active' ? (
            <Button size="small" loading={busy === 'pause'} onClick={() => onAction(grant.id, 'pause')}>Pause</Button>
          ) : null}
          {!isAdmin && grant.status === 'paused' ? (
            <Button size="small" loading={busy === 'resume'} onClick={() => onAction(grant.id, 'resume')}>Resume</Button>
          ) : null}
          <Popconfirm
            title="Disconnect this AI client?"
            description="It will lose access immediately. You can connect again from Claude later."
            okText="Disconnect"
            okType="danger"
            cancelText="Keep"
            onConfirm={() => onAction(grant.id, 'revoke')}
          >
            <Button size="small" danger loading={busy === 'revoke'} data-testid={`revoke-${grant.id}`}>Disconnect</Button>
          </Popconfirm>
        </Space>
      )}
    >
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Text type="secondary">
          Returns to
          {' '}
          <Text code>{grant.redirectHost || '?'}</Text>
          {' · connected '}
          {formatWhen(grant.createdAt)}
          {' · last used '}
          {formatWhen(grant.lastUsedAt)}
          {grant.revokedAt ? ` · disconnected ${formatWhen(grant.revokedAt)}` : ''}
        </Text>
        <div>
          <Text strong>Can</Text>
          <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
            {scopes.filter((s) => s !== 'offline_access').map((s) => (
              <li key={s}>{SCOPE_DESCRIPTIONS[s] || s}</li>
            ))}
          </ul>
        </div>
        <div>
          <Text strong>Accounts and teams included</Text>
          <div>
            {teams.length === 0 ? (
              <Text type="secondary">Only this person&apos;s own data</Text>
            ) : teams.map((id) => (
              <Tag key={id}>{clientNames[id] || id}</Tag>
            ))}
          </div>
        </div>
      </Space>
    </Card>
  );
}

// /client/manage/:clientId/ai-connections  (also /admin/clients/:clientId/ai-connections)
export default function AiConnectionsScreen(props) {
  const clientId = props.match && props.match.params ? props.match.params.clientId : null;
  const isAdmin = !!props.isAdmin;
  const [clientLoading, client] = useClient(isAdmin ? clientId : null);
  const [authLoading, user] = useCurrentUser({ signInAnonymously: false });
  const mcpLive = useMcpLive();

  // Whose connections: the signed-in person, or (admin view) the client's owner.
  const subjectUid = isAdmin ? (client?.ownerId || null) : (user?.uid || null);
  const grants = useMyOAuthGrants(subjectUid);
  const accessible = useAccessibleClients(subjectUid);
  const clientNames = useMemo(() => {
    const m = {};
    (accessible || []).forEach((c) => { m[c.id] = c.displayName || c.id; });
    return m;
  }, [accessible]);

  const [busy, setBusy] = useState({}); // grantId -> action in flight
  const [error, setError] = useState(null);

  const onAction = async (grantId, action) => {
    setBusy((b) => ({ ...b, [grantId]: action }));
    setError(null);
    try {
      await sendGrantAction(grantId, action);
      // The Firestore listener repaints the card with the server-authored status.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy((b) => ({ ...b, [grantId]: null }));
    }
  };

  const loading = authLoading || (isAdmin && clientLoading);
  const live = (grants || []).filter((g) => g.status !== 'revoked');
  const past = (grants || []).filter((g) => g.status === 'revoked');

  return (
    <div style={{ padding: '16px', maxWidth: 640 }}>
      <PageHeader
        ghost={false}
        title="AI connections"
        subTitle={isAdmin ? "This client's owner and the AI assistants connected to their account" : 'The AI assistants connected to your Strikepay account'}
        onBack={() => window.history.back()}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      />

      {!isAdmin ? <SetupCard live={mcpLive} /> : null}

      {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Could not update the connection" description={error} /> : null}

      {loading || grants === null ? (
        <Spin />
      ) : (
        <>
          <Title level={5}>Connected now</Title>
          {live.length === 0 ? (
            <Empty description={isAdmin ? 'No AI connections for this account' : 'Nothing connected yet'} style={{ marginBottom: 16 }} />
          ) : live.map((g) => (
            <GrantCard key={g.id} grant={g} clientNames={clientNames} isAdmin={isAdmin} onAction={onAction} busy={busy[g.id]} />
          ))}
          {past.length > 0 ? (
            <>
              <Title level={5} style={{ marginTop: 16 }}>Disconnected</Title>
              {past.map((g) => (
                <GrantCard key={g.id} grant={g} clientNames={clientNames} isAdmin={isAdmin} onAction={onAction} busy={null} />
              ))}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
